import React, { useState, useEffect } from 'react';
import { Column, User, Project, Client } from '../types';
import { getColumns, createColumn, updateColumn, deleteColumn, getUsers, createUser, updateUser, deleteUser, getProjects, getClients } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const HIDDEN_KEY = 'nwc_hidden_columns';

function loadHidden(): Set<string> {
  try {
    const stored = localStorage.getItem(HIDDEN_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch { return new Set(); }
}

function saveHidden(ids: Set<string>) {
  localStorage.setItem(HIDDEN_KEY, JSON.stringify([...ids]));
}

const COLOR_PRESETS = [
  '#64748B', '#2563EB', '#D97706', '#16A34A', '#DC2626',
  '#7C3AED', '#0891B2', '#DB2777',
];

// ── Project Access Picker ──────────────────────────────────────────────────
interface ProjectAccessPickerProps {
  clients: Client[];
  projects: Project[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

function ProjectAccessPicker({ clients, projects, selectedIds, onChange }: ProjectAccessPickerProps) {
  const projectsByClient = clients.map((c) => ({
    client: c,
    projects: projects.filter((p) => p.clientId === c.id),
  })).filter((g) => g.projects.length > 0);

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  const toggleClient = (clientProjectIds: string[]) => {
    const allSelected = clientProjectIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      onChange(selectedIds.filter((id) => !clientProjectIds.includes(id)));
    } else {
      onChange(Array.from(new Set([...selectedIds, ...clientProjectIds])));
    }
  };

  if (projectsByClient.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#94A3B8', padding: '12px 14px', background: '#F8FAFC', border: '1px dashed #E2E8F0', borderRadius: 8 }}>
        No projects yet. Create projects first, then come back to grant access.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto', padding: 10, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
      {projectsByClient.map(({ client, projects: clientProjects }) => {
        const clientIds = clientProjects.map((p) => p.id);
        const allSelected = clientIds.every((id) => selectedIds.includes(id));
        const someSelected = clientIds.some((id) => selectedIds.includes(id));
        return (
          <div key={client.id} style={{ background: 'white', borderRadius: 6, padding: '8px 10px', border: '1px solid #E2E8F0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, color: '#1E293B' }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                onChange={() => toggleClient(clientIds)}
              />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: client.color, flexShrink: 0 }} />
              {client.name}
              <span style={{ fontSize: 10, fontWeight: 500, color: '#94A3B8' }}>
                ({clientIds.filter((id) => selectedIds.includes(id)).length}/{clientIds.length})
              </span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 22 }}>
              {clientProjects.map((p) => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: '#475569' }}>
                  <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggle(p.id)} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Sortable row wrapper ──────────────────────────────────────────────────
interface SortableColumnRowProps {
  col: Column;
  editingCol: string | null;
  editName: string;
  editColor: string;
  deletingId: string | null;
  hiddenColIds: Set<string>;
  onEdit: (id: string, name: string, color: string) => void;
  onCancelEdit: () => void;
  onSave: (id: string) => void;
  onDelete: (id: string) => void;
  onCancelDelete: () => void;
  onToggleVisibility: (id: string) => void;
  onEditNameChange: (v: string) => void;
  onEditColorChange: (v: string) => void;
}

function SortableColumnRow({
  col,
  editingCol,
  editName,
  editColor,
  deletingId,
  hiddenColIds,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onCancelDelete,
  onToggleVisibility,
  onEditNameChange,
  onEditColorChange,
}: SortableColumnRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: col.id });

  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: isDragging ? '#EFF6FF' : '#F8FAFC',
    borderRadius: 10,
    border: isDragging ? '1px solid #93C5FD' : '1px solid #E2E8F0',
    boxShadow: isDragging ? '0 4px 16px rgba(37,99,235,0.15)' : undefined,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: editingCol === col.id ? 'default' : 'inherit',
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* Drag handle */}
      {editingCol !== col.id && (
        <div
          {...listeners}
          {...attributes}
          title="Drag to reorder"
          style={{
            cursor: 'grab',
            color: '#CBD5E1',
            fontSize: 18,
            lineHeight: 1,
            userSelect: 'none',
            flexShrink: 0,
            paddingRight: 2,
          }}
        >
          ⠿
        </div>
      )}
      {editingCol === col.id ? (
        <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={editName}
            onChange={(e) => onEditNameChange(e.target.value)}
            style={{ flex: 1, minWidth: 120 }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 4 }}>
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onEditColorChange(c)}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: c,
                  border: editColor === c ? '2px solid #0F172A' : '1px solid transparent',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
          <button onClick={() => onSave(col.id)} className="btn btn-primary btn-sm">Save</button>
          <button onClick={onCancelEdit} className="btn btn-ghost btn-sm">Cancel</button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: col.color, flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: 14, color: hiddenColIds.has(col.id) ? '#94A3B8' : '#1E293B' }}>{col.name}</span>
            {hiddenColIds.has(col.id) && (
              <span style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>HIDDEN</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              onClick={() => onToggleVisibility(col.id)}
              className="btn btn-ghost btn-sm"
              title={hiddenColIds.has(col.id) ? 'Show on dashboard' : 'Hide from dashboard'}
              style={{
                color: hiddenColIds.has(col.id) ? '#94A3B8' : '#2563EB',
                padding: '4px 8px',
                fontSize: 16,
              }}
            >
              {hiddenColIds.has(col.id) ? '🚫' : '👁️'}
            </button>
            <button
              onClick={() => onEdit(col.id, col.name, col.color)}
              className="btn btn-secondary btn-sm"
            >
              Edit
            </button>
            <button onClick={() => onDelete(col.id)} className="btn btn-danger btn-sm">
              {deletingId === col.id ? 'Confirm?' : 'Delete'}
            </button>
            {deletingId === col.id && (
              <button onClick={onCancelDelete} className="btn btn-ghost btn-sm">Cancel</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const { user: currentUser } = useAuth();

  // Column visibility
  const [hiddenColIds, setHiddenColIds] = useState<Set<string>>(loadHidden);

  const toggleColVisibility = (id: string) => {
    setHiddenColIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveHidden(next);
      return next;
    });
  };

  // Columns
  const [columns, setColumns] = useState<Column[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColColor, setNewColColor] = useState('#2563EB');
  const [editingCol, setEditingCol] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Users
  const [users, setUsers] = useState<User[]>([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'client'>('admin');
  const [newUserProjectIds, setNewUserProjectIds] = useState<string[]>([]);
  const [userError, setUserError] = useState('');
  const [userSaving, setUserSaving] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Edit-user state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserRole, setEditUserRole] = useState<'admin' | 'client'>('admin');
  const [editUserProjectIds, setEditUserProjectIds] = useState<string[]>([]);
  const [editUserPassword, setEditUserPassword] = useState('');

  // Projects + Clients (for the project access picker)
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);

  useEffect(() => {
    Promise.all([getColumns(), getUsers(), getProjects(), getClients()])
      .then(([cols, usrs, projs, clis]) => {
        setColumns(cols);
        setUsers(usrs);
        setAllProjects(projs);
        setAllClients(clis);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSaving(true);
    try {
      const user = await createUser({
        name: newUserName.trim(),
        email: newUserEmail.trim(),
        password: newUserPassword,
        role: newUserRole,
        projectIds: newUserRole === 'client' ? newUserProjectIds : [],
      });
      setUsers((prev) => [...prev, user].sort((a, b) => a.name.localeCompare(b.name)));
      setNewUserName(''); setNewUserEmail(''); setNewUserPassword('');
      setNewUserRole('admin'); setNewUserProjectIds([]);
      setShowAddUser(false);
    } catch (err: any) {
      setUserError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setUserSaving(false);
    }
  };

  const startEditUser = (u: User) => {
    setEditingUserId(u.id);
    setEditUserName(u.name);
    setEditUserRole(((u.role as 'admin' | 'client') || 'admin'));
    setEditUserProjectIds(u.projectIds || []);
    setEditUserPassword('');
    setUserError('');
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
    setEditUserPassword('');
  };

  const handleSaveEditUser = async () => {
    if (!editingUserId) return;
    setUserError('');
    setUserSaving(true);
    try {
      const updated = await updateUser(editingUserId, {
        name: editUserName.trim() || undefined,
        role: editUserRole,
        projectIds: editUserRole === 'client' ? editUserProjectIds : [],
        password: editUserPassword.length >= 8 ? editUserPassword : undefined,
      });
      setUsers((prev) => prev.map((u) => (u.id === editingUserId ? { ...u, ...updated } : u)));
      cancelEditUser();
    } catch (err: any) {
      setUserError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setUserSaving(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (deletingUserId !== id) { setDeletingUserId(id); return; }
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setDeletingUserId(null);
    } catch (err: any) {
      setUserError(err.response?.data?.error || 'Failed to delete user');
      setDeletingUserId(null);
    }
  };

  const handleAddColumn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newColName.trim()) return;
    try {
      const col = await createColumn({ name: newColName.trim(), color: newColColor });
      setColumns((prev) => [...prev, col]);
      setNewColName('');
      setNewColColor('#2563EB');
      setShowAdd(false);
    } catch {
      setError('Failed to create column');
    }
  };

  const handleUpdateColumn = async (id: string) => {
    try {
      const col = await updateColumn(id, { name: editName, color: editColor });
      setColumns((prev) => prev.map((c) => (c.id === id ? col : c)));
      setEditingCol(null);
    } catch {
      setError('Failed to update column');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columns.findIndex((c) => c.id === active.id);
    const newIndex = columns.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(columns, oldIndex, newIndex).map((c, i) => ({ ...c, position: i }));
    setColumns(reordered);

    // Persist changed positions
    setReordering(true);
    try {
      await Promise.all(
        reordered
          .filter((c, i) => columns[i]?.id !== c.id) // only changed ones
          .map((c) => updateColumn(c.id, { position: c.position }))
      );
    } catch {
      setError('Failed to save new order');
    } finally {
      setReordering(false);
    }
  };

  const handleDeleteColumn = async (id: string) => {
    if (deletingId !== id) { setDeletingId(id); return; }
    try {
      await deleteColumn(id);
      setColumns((prev) => prev.filter((c) => c.id !== id));
      setDeletingId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Cannot delete column');
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: 700, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px', marginBottom: 8 }}>Settings</h1>
      <p style={{ color: '#64748B', fontSize: 14, marginBottom: 32 }}>Configure your board stages and workflow</p>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }}>×</button>
        </div>
      )}

      {/* Board Columns */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>Board Stages</h2>
            <p style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>
              Define the stages cards move through. Position affects column order.
            </p>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm">+ Add Stage</button>
        </div>

        {/* Add form */}
        {showAdd && (
          <form onSubmit={handleAddColumn} className="fade-in" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <label>Stage Name</label>
              <input
                value={newColName}
                onChange={(e) => setNewColName(e.target.value)}
                placeholder="e.g., In Review"
                autoFocus
                required
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>Color</label>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColColor(c)}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: c,
                      border: newColColor === c ? '3px solid #0F172A' : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setShowAdd(false)} className="btn btn-secondary btn-sm">Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm">Create Stage</button>
            </div>
          </form>
        )}

        {/* Column list */}
        {reordering && (
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className="spinner" style={{ width: 12, height: 12 }} /> Saving order…
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={columns.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {columns.map((col) => (
                <SortableColumnRow
                  key={col.id}
                  col={col}
                  editingCol={editingCol}
                  editName={editName}
                  editColor={editColor}
                  deletingId={deletingId}
                  hiddenColIds={hiddenColIds}
                  onEdit={(id, name, color) => { setEditingCol(id); setEditName(name); setEditColor(color); }}
                  onCancelEdit={() => setEditingCol(null)}
                  onSave={handleUpdateColumn}
                  onDelete={handleDeleteColumn}
                  onCancelDelete={() => setDeletingId(null)}
                  onToggleVisibility={toggleColVisibility}
                  onEditNameChange={setEditName}
                  onEditColorChange={setEditColor}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Team Members */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>Team Members</h2>
            <p style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>Manage who can log into the dashboard and be assigned tasks.</p>
          </div>
          <button onClick={() => setShowAddUser(true)} className="btn btn-primary btn-sm">+ Add Member</button>
        </div>

        {userError && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
            {userError}
            <button onClick={() => setUserError('')} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }}>×</button>
          </div>
        )}

        {/* Add user form */}
        {showAddUser && (
          <form onSubmit={handleAddUser} className="fade-in" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label>Full Name</label>
                <input value={newUserName} onChange={(e) => setNewUserName(e.target.value)} placeholder="e.g., Allen Smith" required autoFocus />
              </div>
              <div>
                <label>Email</label>
                <input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="allen@newwavecreative.io" required />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label>Password</label>
                <input type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} placeholder="Min 8 characters" minLength={8} required />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label>Role</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['admin', 'client'] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setNewUserRole(r)}
                      style={{
                        padding: '6px 14px', borderRadius: 100,
                        border: `1px solid ${newUserRole === r ? '#2563EB' : '#E2E8F0'}`,
                        background: newUserRole === r ? '#EFF6FF' : 'white',
                        color: newUserRole === r ? '#1D4ED8' : '#64748B',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                      }}
                    >
                      {r === 'admin' ? 'Admin (full access)' : 'Client (read-only)'}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#64748B', marginTop: 6 }}>
                  {newUserRole === 'admin'
                    ? 'Admins see and edit everything.'
                    : 'Clients only see the projects you grant below. They can comment but not edit.'}
                </p>
              </div>
              {newUserRole === 'client' && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label>Project access</label>
                  <ProjectAccessPicker
                    clients={allClients}
                    projects={allProjects}
                    selectedIds={newUserProjectIds}
                    onChange={setNewUserProjectIds}
                  />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => { setShowAddUser(false); setUserError(''); }} className="btn btn-secondary btn-sm">Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={userSaving}>
                {userSaving ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </form>
        )}

        {/* User list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {users.map((u) => (
            <div key={u.id} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#F8FAFC', borderRadius: editingUserId === u.id ? '10px 10px 0 0' : 10, border: '1px solid #E2E8F0', borderBottom: editingUserId === u.id ? 'none' : '1px solid #E2E8F0' }}
              >
                <div
                  style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}
                >
                  {u.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {u.name} {u.id === currentUser?.id && <span style={{ fontSize: 11, color: '#2563EB', fontWeight: 500 }}>(you)</span>}
                    <span
                      style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                        background: u.role === 'client' ? '#FEF3C7' : '#DBEAFE',
                        color: u.role === 'client' ? '#92400E' : '#1D4ED8',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}
                    >
                      {(u.role || 'admin') === 'client' ? 'Client' : 'Admin'}
                    </span>
                    {u.role === 'client' && (
                      <span style={{ fontSize: 11, color: '#64748B' }}>
                        {(u.projectIds?.length || 0)} project{(u.projectIds?.length || 0) === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748B' }}>{u.email}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => (editingUserId === u.id ? cancelEditUser() : startEditUser(u))}
                    className="btn btn-secondary btn-sm"
                  >
                    {editingUserId === u.id ? 'Close' : 'Edit'}
                  </button>
                  {u.id !== currentUser?.id && (
                    <>
                      <button onClick={() => handleDeleteUser(u.id)} className="btn btn-danger btn-sm">
                        {deletingUserId === u.id ? 'Confirm?' : 'Remove'}
                      </button>
                      {deletingUserId === u.id && (
                        <button onClick={() => setDeletingUserId(null)} className="btn btn-ghost btn-sm">Cancel</button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Inline edit panel */}
              {editingUserId === u.id && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label>Full Name</label>
                      <input value={editUserName} onChange={(e) => setEditUserName(e.target.value)} />
                    </div>
                    <div>
                      <label>Reset Password (optional)</label>
                      <input type="password" value={editUserPassword} onChange={(e) => setEditUserPassword(e.target.value)} placeholder="Leave blank to keep current" minLength={8} />
                    </div>
                  </div>
                  <div>
                    <label>Role</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {(['admin', 'client'] as const).map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setEditUserRole(r)}
                          style={{
                            padding: '6px 14px', borderRadius: 100,
                            border: `1px solid ${editUserRole === r ? '#2563EB' : '#E2E8F0'}`,
                            background: editUserRole === r ? '#EFF6FF' : 'white',
                            color: editUserRole === r ? '#1D4ED8' : '#64748B',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                          }}
                        >
                          {r === 'admin' ? 'Admin (full access)' : 'Client (read-only)'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {editUserRole === 'client' && (
                    <div>
                      <label>Project access</label>
                      <ProjectAccessPicker
                        clients={allClients}
                        projects={allProjects}
                        selectedIds={editUserProjectIds}
                        onChange={setEditUserProjectIds}
                      />
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={cancelEditUser} className="btn btn-secondary btn-sm">Cancel</button>
                    <button type="button" onClick={handleSaveEditUser} disabled={userSaving} className="btn btn-primary btn-sm">
                      {userSaving ? 'Saving...' : 'Save changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info card */}
      <div
        style={{
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 12,
          padding: '16px 18px',
          fontSize: 13,
          color: '#1E40AF',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>💡 Pro tip</div>
        <div style={{ lineHeight: 1.5 }}>
          Drag the <strong>⠿</strong> handle on any stage row to reorder it. Changes are saved automatically and reflected on the board immediately.
        </div>
      </div>
    </div>
  );
}
